/**
 * Active le mode édition organigramme sur l’accueil national, une direction ou une fiche équipe.
 * Manageur : périmètre de GET /v1/me/org-positions (ancrage direction, domaine, agence ou équipe).
 * L’accueil national reste fermé aux manageurs. Un admin n’y entre que s’il a au moins une direction de rattachement.
 */
import type { RouteLocationNormalizedLoaded } from 'vue-router'
import type {
  ManagerOrganigramEditScope,
  MeOrgPosition,
} from '~/composables/equipe/useMyOrgPositions'
import {
  canUseOrganigrammeEditMode,
  isDeveloper,
  isLegacyAdmin,
  isSuperAdmin,
} from './abilities'
import { inferOrgAnchorLevelForPosition } from './profileRightsMatrix'

const RESERVED_ROOT_SEGMENTS = new Set([
  'admin',
  'parametres',
  'dev',
  'mentions-legals',
  'accessibilite',
  'changelog',
  'login',
  'auth',
])

function trimOrgSegment(value: string | undefined | null): string {
  if (typeof value !== 'string') {
    return ''
  }
  return value.trim()
}

/**
 * Pages où le mode édition organigramme a un sens : accueil national, direction, fiche équipe (4 segments).
 * Exclut l’administration, le labo, la connexion, etc.
 */
export function isOrganigrammeContentRoutePath(path: string): boolean {
  const normalized = path.replace(/\/$/, '') || '/'
  if (normalized === '/') {
    return true
  }
  const segments = normalized.split('/').filter(Boolean)
  if (segments.length === 0) {
    return true
  }
  if (RESERVED_ROOT_SEGMENTS.has(segments[0] ?? '')) {
    return false
  }
  return segments.length === 1 || segments.length >= 4
}

type EquipeRouteParsed = {
  dir: string
  dom: string
  age: string
  equ: string
  isDomaineTeam: boolean
  isAgenceLeaderEquipe: boolean
}

function parseEquipeRouteParams(route: RouteLocationNormalizedLoaded): EquipeRouteParsed {
  const dir = trimOrgSegment(String(route.params.direction ?? ''))
  const dom = trimOrgSegment(String(route.params.domaine ?? ''))
  const age = trimOrgSegment(String(route.params.agence ?? ''))
  const equ = trimOrgSegment(String(route.params.equipe ?? ''))
  const isDomaineTeam = dom !== '' && dom === age && age === equ
  const isAgenceLeaderEquipe = !isDomaineTeam && age !== '' && equ === age
  return { dir, dom, age, equ, isDomaineTeam, isAgenceLeaderEquipe }
}

function isDirectionLandingRoute(route: RouteLocationNormalizedLoaded): boolean {
  const { dir, dom, age, equ } = parseEquipeRouteParams(route)
  return dir !== '' && dom === '' && age === '' && equ === ''
}

function isEquipePageRoute(route: RouteLocationNormalizedLoaded): boolean {
  const { dir, dom, age, equ } = parseEquipeRouteParams(route)
  return dir !== '' && dom !== '' && age !== '' && equ !== ''
}

function equipePageMatchesPosition(
  routeParsed: EquipeRouteParsed,
  position: MeOrgPosition
): boolean {
  const pDom = trimOrgSegment(position.domLib)
  const pAge = trimOrgSegment(position.ageLib)
  const pEqu = trimOrgSegment(position.equLib)

  if (routeParsed.isDomaineTeam) {
    return routeParsed.equ === pEqu && pEqu !== ''
  }
  if (routeParsed.isAgenceLeaderEquipe) {
    return (
      routeParsed.dom === pDom &&
      routeParsed.age === pAge &&
      routeParsed.equ === pEqu &&
      pEqu !== ''
    )
  }
  return (
    routeParsed.dom === pDom &&
    routeParsed.age === pAge &&
    routeParsed.equ === pEqu &&
    pEqu !== ''
  )
}

function normalizeScopeDirToken(value: string): string {
  return value.trim().toLowerCase()
}

/**
 * Périmètre serveur (leader domaine / agence) : toute fiche équipe du domaine ou de l’agence concernée.
 */
function managerOrganigramEditScopeAllowsEquipeRoute(
  routeParsed: EquipeRouteParsed,
  scope: ManagerOrganigramEditScope | null | undefined
): boolean {
  if (scope === undefined || scope === null) {
    return false
  }
  const routeDir = normalizeScopeDirToken(routeParsed.dir)
  const routeDom = normalizeScopeDirToken(routeParsed.dom)
  const routeAge = normalizeScopeDirToken(routeParsed.age)
  for (const domainEntry of scope.domainLeaders ?? []) {
    if (
      normalizeScopeDirToken(domainEntry.dirLib) === routeDir &&
      normalizeScopeDirToken(domainEntry.domLib) === routeDom
    ) {
      return true
    }
  }
  for (const agenceEntry of scope.agenceLeaders ?? []) {
    if (
      normalizeScopeDirToken(agenceEntry.dirLib) === routeDir &&
      normalizeScopeDirToken(agenceEntry.domLib) === routeDom &&
      normalizeScopeDirToken(agenceEntry.ageLib) === routeAge
    ) {
      return true
    }
  }
  return false
}

/**
 * Manageur applicatif : pas d’édition sur l’accueil national.
 * Page direction : leader de cette direction, ou leader d’un domaine de cette direction.
 * Fiche équipe : enfants de l’ancrage (direction, domaine, agence) ou membre de cette équipe.
 */
function canManagerActivateOrganigramEditOnRoute(
  route: RouteLocationNormalizedLoaded,
  positions: MeOrgPosition[],
  managerOrganigramEditScope: ManagerOrganigramEditScope | null = null
): boolean {
  if (positions.length === 0 || isNationalHomePath(route)) {
    return false
  }
  if (isDirectionLandingRoute(route)) {
    return managerMayEditDirectionLanding(route, positions, managerOrganigramEditScope)
  }
  if (!isEquipePageRoute(route)) {
    return false
  }
  return managerMayEditEquipePage(route, positions, managerOrganigramEditScope)
}

/**
 * Admin OIDC : accès édition sur l’accueil national ou sur les pages d’une direction où l’utilisateur est rattaché dans l’organigramme (périmètre aligné sur GET /v1/me).
 */
function canScopedAdminActivateOrganigramEditOnRoute(
  path: string,
  route: RouteLocationNormalizedLoaded,
  directionAdminDirLibs: string[]
): boolean {
  if (directionAdminDirLibs.length === 0) {
    return false
  }
  const scopeSet = new Set(directionAdminDirLibs.map((dirLib) => normalizeScopeDirToken(dirLib)))
  const pathNormalized = (path || route.path).replace(/\/$/, '') || '/'
  if (pathNormalized === '/') {
    return true
  }
  const dir = directionLibFromOrganigrammePath(pathNormalized, route)
  if (dir === '') {
    return false
  }
  return scopeSet.has(normalizeScopeDirToken(dir))
}

function directionLibFromOrganigrammePath(
  path: string,
  route: RouteLocationNormalizedLoaded,
): string {
  const first = path.split('/').filter(Boolean)[0] ?? ''
  if (first !== '' && !RESERVED_ROOT_SEGMENTS.has(first)) {
    return trimOrgSegment(first)
  }
  return trimOrgSegment(String(route.params.direction ?? ''))
}

type OrganigrammeEditRouteAccess = {
  directionAdminDirLibs?: string[]
  managerOrganigramEditScope?: ManagerOrganigramEditScope | null
}

export function canActivateOrganigrammeEditOnRoute(
  path: string,
  route: RouteLocationNormalizedLoaded,
  positions: MeOrgPosition[],
  roles: string[],
  access: OrganigrammeEditRouteAccess = {},
): boolean {
  const directionAdminDirLibs = access.directionAdminDirLibs ?? []
  const managerOrganigramEditScope = access.managerOrganigramEditScope ?? null
  if (!canUseOrganigrammeEditMode(roles, directionAdminDirLibs)) {
    return false
  }
  if (!isOrganigrammeContentRoutePath(path)) {
    return false
  }
  if (isSuperAdmin(roles)) {
    return true
  }
  if (isDeveloper(roles)) {
    return true
  }
  if (isLegacyAdmin(roles)) {
    return canScopedAdminActivateOrganigramEditOnRoute(path, route, directionAdminDirLibs)
  }
  return canManagerActivateOrganigramEditOnRoute(route, positions, managerOrganigramEditScope)
}

function isNationalHomePath(route: RouteLocationNormalizedLoaded): boolean {
  const pathNormalized = route.path.replace(/\/$/, '') || '/'
  return pathNormalized === '/'
}

function managerMayEditDirectionLanding(
  route: RouteLocationNormalizedLoaded,
  positions: MeOrgPosition[],
  scope: ManagerOrganigramEditScope | null,
): boolean {
  const dirRaw = trimOrgSegment(String(route.params.direction ?? ''))
  const dir = normalizeScopeDirToken(dirRaw)
  if (scopeListsLeaderInDirection(scope?.directionLeaders, dir)) {
    return true
  }
  if (scopeListsLeaderInDirection(scope?.domainLeaders, dir)) {
    return true
  }
  return positionsAnchorDirectionLanding(positions, dirRaw)
}

function scopeListsLeaderInDirection(
  entries: Array<{ dirLib: string }> | undefined,
  dir: string,
): boolean {
  return (entries ?? []).some((entry) => normalizeScopeDirToken(entry.dirLib) === dir)
}

function positionsAnchorDirectionLanding(positions: MeOrgPosition[], dirRaw: string): boolean {
  return positions.some((position) => positionAnchorsDirectionLanding(position, dirRaw))
}

function positionAnchorsDirectionLanding(position: MeOrgPosition, dirRaw: string): boolean {
  if (trimOrgSegment(position.dirLib) !== dirRaw) {
    return false
  }
  const anchor = inferOrgAnchorLevelForPosition(position)
  return anchor === 'direction' || anchor === 'domaine'
}

function managerMayEditEquipePage(
  route: RouteLocationNormalizedLoaded,
  positions: MeOrgPosition[],
  scope: ManagerOrganigramEditScope | null,
): boolean {
  const routeParsed = parseEquipeRouteParams(route)
  if (managerOrganigramEditScopeAllowsEquipeRoute(routeParsed, scope)) {
    return true
  }
  return positionsAllowEquipePage(positions, routeParsed)
}

function positionsAllowEquipePage(positions: MeOrgPosition[], routeParsed: EquipeRouteParsed): boolean {
  return positions.some((position) => positionAllowsEquipePage(position, routeParsed))
}

function positionAllowsEquipePage(position: MeOrgPosition, routeParsed: EquipeRouteParsed): boolean {
  if (trimOrgSegment(position.dirLib) !== routeParsed.dir) {
    return false
  }
  return anchorAllowsEquipePage(position, routeParsed)
}

function anchorAllowsEquipePage(position: MeOrgPosition, routeParsed: EquipeRouteParsed): boolean {
  const anchor = inferOrgAnchorLevelForPosition(position)
  if (anchor === 'direction') {
    return true
  }
  if (anchor === 'domaine') {
    return domaineAnchorCoversRoute(position, routeParsed)
  }
  if (anchor === 'agence') {
    return agenceAnchorCoversRoute(position, routeParsed)
  }
  if (anchor === 'equipe' || anchor === 'section') {
    return equipePageMatchesPosition(routeParsed, position)
  }
  return false
}

function domaineAnchorCoversRoute(position: MeOrgPosition, routeParsed: EquipeRouteParsed): boolean {
  const pDom = trimOrgSegment(position.domLib)
  return pDom !== '' && routeParsed.dom === pDom
}

function agenceAnchorCoversRoute(position: MeOrgPosition, routeParsed: EquipeRouteParsed): boolean {
  const pDom = trimOrgSegment(position.domLib)
  const pAge = trimOrgSegment(position.ageLib)
  return pDom === routeParsed.dom && pAge === routeParsed.age
}
